<style scoped>
/* Flow layout inside the app's #content column (the 250px sidebar offset is
 * handled globally). The screen is the header, the weather across the full
 * width, then one row of [ workbench cards | map | technician roster ]. Above
 * xl the row fills the screen: the weather keeps its natural height and the row
 * takes the rest, so the page stays one screen. Below xl the parts stack,
 * weather first, and the map takes a fixed height because it has no row to
 * fill. */
.home-screen {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.home-head {
	display: flex;
	align-items: baseline;
	gap: 8px;
}

.home-body {
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
}

/* The four workbenches read as a 2x2 block: two even columns, two rows sharing
 * whatever height the row gives them. minmax(0, 1fr) rather than 1fr so a wide
 * card can never push its column past its share. */
.home-cards {
	display: grid;
	gap: 8px;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	grid-template-rows: repeat(2, minmax(0, 1fr));
	min-width: 0;
}

.home-map {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.home-aside {
	min-width: 0;
}

.home-placeholder {
	background: #f8f9fa;
	border: 1px dashed #ced4da;
}

@media (min-width: 1200px) {
	.home-screen {
		height: calc(100vh - 20px); /* #content has 10px padding top/bottom */
	}

	/* The weather is the one full-width band; everything else shares the row
	   beneath it. */
	.home-weather {
		flex: 0 0 auto;
	}

	.home-body {
		flex: 1 1 auto;
		flex-direction: row;
		min-height: 0;
	}

	/* Cards and map split the space left of the roster evenly, so the map's
	   footprint matches the 2x2 block beside it rather than running long and
	   thin. */
	.home-cards,
	.home-map {
		flex: 1 1 0;
		min-height: 0;
	}

	/* The roster runs the full height of the row, weather above it. */
	.home-aside {
		display: flex;
		flex: 0 0 300px;
		flex-direction: column;
		min-height: 0;
	}
}

@media (max-width: 1199.98px) {
	.home-map {
		height: 420px;
	}
}
</style>

<template>
	<div class="home-screen">
		<div class="home-head">
			<h1 class="h5 fw-bold mb-0">Home</h1>
			<span class="badge rounded-pill text-bg-light border"
				>Weather, map and communications live · other cards on placeholder
				data</span
			>
			<span class="text-muted small text-truncate">
				District situation awareness — pick a workbench, or read the district
				around them.
			</span>
		</div>

		<!-- The weather spans the screen; it is absent until it has been read, and
		     stays absent when the payload has no weather, rather than showing
		     invented figures. -->
		<WeatherCard
			v-if="weatherCard"
			class="home-weather"
			:weather="weatherCard"
		/>

		<div class="home-body">
			<!-- Workbenches -->
			<div class="home-cards">
				<!-- Communications -->
				<DashboardCard
					title="Communications"
					subtitle="Awaiting a reply"
					to="/_/communication"
				>
					<template v-if="communicationsCard">
						<div class="d-flex align-items-baseline gap-2">
							<span class="display-6 fw-bold">
								<LocalizedNumber :value="communicationsCard.total" />
							</span>
							<span class="small text-muted">awaiting</span>
						</div>
						<ul
							v-if="communicationsCard.channels.length"
							class="list-unstyled small mb-0 mt-1"
						>
							<li
								v-for="channel in communicationsCard.channels"
								:key="channel.channel"
								class="d-flex justify-content-between align-items-baseline gap-2"
							>
								<span class="text-muted">{{
									channelLabel(channel.channel)
								}}</span>
								<span class="fw-semibold">
									<LocalizedNumber :value="channel.count" />
								</span>
							</li>
						</ul>
					</template>
					<div
						v-else
						class="home-placeholder rounded text-center p-2 text-muted"
					>
						<i
							class="bi bi-hourglass-split d-block mb-1"
							aria-hidden="true"
						></i>
						<div class="fw-semibold text-body">Not available</div>
						<div class="small">Awaiting communications could not be read.</div>
					</div>

					<template #footer>
						<div
							class="d-flex align-items-center justify-content-between gap-1"
						>
							<span class="small text-muted">{{ oldestWaitingLabel }}</span>
							<RouterLink
								class="btn btn-sm btn-outline-primary home-card-action"
								to="/_/communication?phone=1"
							>
								<i class="bi bi-telephone-plus me-1" aria-hidden="true"></i>
								Take a call
							</RouterLink>
						</div>
					</template>
				</DashboardCard>

				<!-- Planning -->
				<DashboardCard
					title="Planning"
					subtitle="Signals &amp; leads"
					to="/_/planning"
				>
					<div class="d-flex justify-content-between align-items-baseline">
						<span class="small text-muted">New signals this week</span>
						<span class="fs-5 fw-bold">
							<LocalizedNumber :value="planningCard.new_signals_this_week" />
						</span>
					</div>
					<div class="d-flex justify-content-between align-items-baseline mt-1">
						<span class="small text-muted">Active leads</span>
						<span class="fs-5 fw-bold">
							<LocalizedNumber :value="planningCard.active_leads" />
						</span>
					</div>
				</DashboardCard>

				<!-- Review -->
				<DashboardCard
					title="Review"
					subtitle="New records today"
					to="/_/review"
				>
					<div class="home-placeholder rounded text-center p-2 text-muted">
						<template v-if="reviewCard.new_records_today === null">
							<i
								class="bi bi-hourglass-split d-block mb-1"
								aria-hidden="true"
							></i>
							<div class="fw-semibold text-body">Not yet developed</div>
							<div class="small">Will list every record added today.</div>
						</template>
						<template v-else>
							<div class="display-6 fw-bold text-body">
								{{ reviewCard.new_records_today }}
							</div>
							<div class="small">new records today</div>
						</template>
					</div>
				</DashboardCard>

				<!-- Surveillance -->
				<DashboardCard
					title="Surveillance"
					subtitle="Traps &amp; vector index"
					to="/_/intelligence"
				>
					<TrendRow label="Trap counts" :trend="surveillanceCard.trap_counts" />
					<TrendRow
						label="Vector index"
						:trend="surveillanceCard.vector_index"
					/>
					<template #footer>
						<span class="small text-muted">
							{{ surveillanceCard.trend_basis }}.
						</span>
					</template>
				</DashboardCard>
			</div>

			<div class="home-map">
				<TechnicianMap
					:technicians="home.payload?.technicians ?? []"
					:wind-lines="home.payload?.wind?.lines ?? []"
					:wind-arrows="home.payload?.wind?.arrows ?? []"
					:boundary="home.payload?.boundary ?? null"
				/>
			</div>

			<div class="home-aside">
				<TechnicianRoster :technicians="home.payload?.technicians ?? []" />
			</div>
		</div>

		<ToastNotification
			:title="toast.title"
			:message="toast.message"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import LocalizedNumber from "@/components/LocalizedNumber.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import DashboardCard from "@/components/home/DashboardCard.vue";
import TechnicianMap from "@/components/home/TechnicianMap.vue";
import TechnicianRoster from "@/components/home/TechnicianRoster.vue";
import TrendRow from "@/components/home/TrendRow.vue";
import WeatherCard from "@/components/home/WeatherCard.vue";
import {
	eggToAdult,
	reviewCard,
	surveillanceCard,
} from "@/components/home/placeholder";
import { useStoreHome } from "@/store/home";
import { apiClient } from "@/client";
import type {
	HomePlanningCard,
	HomeWeatherCard,
	MapReviewQueryEnvelope,
} from "@/type/api";

const home = useStoreHome();

// The Planning card is live: new Signals this week and open Leads, read from
// the resources the Planning workbench writes. It starts at zero rather than a
// placeholder figure, so a card that has not read yet never claims a number.
const planningCard = ref<HomePlanningCard>({
	new_signals_this_week: 0,
	active_leads: 0,
});

async function loadPlanningCard() {
	const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
	try {
		const [signals, leads] = await Promise.all([
			apiClient.JSONQuery<MapReviewQueryEnvelope<unknown>>("/api/signal", {
				limit: 1,
				filter: [{ property: "created", operator: "after", value: weekAgo }],
			}),
			apiClient.JSONQuery<MapReviewQueryEnvelope<unknown>>("/api/lead", {
				limit: 1,
				filter: [{ property: "state", operator: "equal", value: "open" }],
			}),
		]);
		planningCard.value = {
			new_signals_this_week: signals.metadata.filtered_total,
			active_leads: leads.metadata.filtered_total,
		};
	} catch {
		showNotification(
			"Planning counts unavailable",
			"The Planning card could not be read just now. The rest of the screen is unaffected.",
		);
	}
}

// The district's awaiting communications: null until the payload has been read,
// and left null when the section could not be, so the card shows its empty
// state rather than a zero it did not measure.
const communicationsCard = computed(() => home.payload?.communications ?? null);

// The split's channels as the operator reads them. A channel this map does not
// know is shown as the payload spells it.
const CHANNEL_LABELS: Record<string, string> = {
	public_report: "Public reports",
	text: "Texts",
	phone_call: "Phone calls",
	email: "Emails",
	paper_letter: "Paper letters",
	in_person: "In person",
	virtual_inspection: "Virtual inspections",
	unknown: "Unrecorded",
};

function channelLabel(channel: string): string {
	return CHANNEL_LABELS[channel] ?? channel;
}

const oldestWaitingLabel = computed(() => {
	const oldest = communicationsCard.value?.oldest_waiting_days;
	if (oldest == null) return communicationsCard.value ? "Nothing waiting" : "";
	return `Oldest waiting ${oldest} day${oldest === 1 ? "" : "s"}`;
});

// WeatherCard still carries the egg-to-adult block, which the payload does not:
// the two are handed over together here.
const weatherCard = computed<HomeWeatherCard | null>(() =>
	home.payload?.weather
		? { ...home.payload.weather, egg_to_adult: eggToAdult }
		: null,
);

// How each section the payload may fail to read is named to the operator.
const UNAVAILABLE_NOTICES: Record<string, { title: string; message: string }> =
	{
		weather: {
			title: "Weather unavailable",
			message:
				"The district's weather could not be read just now. The map and the workbenches are unaffected.",
		},
		wind: {
			title: "Wind unavailable",
			message:
				"The wind over the district could not be read just now. The map still shows the district and the technicians.",
		},
		communications: {
			title: "Communications unavailable",
			message:
				"The district's awaiting communications could not be read just now. The map and the other cards are unaffected.",
		},
	};

const toast = ref({ title: "", message: "" });
const toastShow = ref(false);

// The toast card shows one message at a time, so the notices queue: a failed
// section must not be hidden behind another one reported at the same moment.
const toastQueue: { title: string; message: string }[] = [];

function nextNotification() {
	const queued = toastQueue.shift();
	if (!queued) return;
	toast.value = queued;
	toastShow.value = true;
}

function showNotification(title: string, message: string) {
	toastQueue.push({ title, message });
	if (!toastShow.value) nextNotification();
}

// Dismissal (by hand or by the toast's own timer) lets the next notice in.
watch(toastShow, (show) => {
	if (!show) nextNotification();
});

onMounted(async () => {
	try {
		const payload = await home.fetchHome();
		// A section the payload could not read is a notice, not a failure: the
		// rest of the screen — boundary, technicians, the workbenches — is live.
		for (const section of payload.unavailable) {
			const notice = UNAVAILABLE_NOTICES[section];
			showNotification(
				notice?.title ?? "Section unavailable",
				notice?.message ?? `The ${section} section could not be read.`,
			);
		}
		void loadPlanningCard();
	} catch {
		showNotification(
			"Home unavailable",
			home.error ?? "Could not load the district's situation.",
		);
	}
});
</script>
